import { FileText, Map, MessageSquare } from 'lucide-react';
import Slide from '@/components/shared/Slide';
import { Separator } from '@/components/ui/separator';
import type { ViewMode } from '@/types/roadmap.types';

interface Props {
  programs: unknown[];
  mode: ViewMode;
}

const deliverables = [
  {
    icon: FileText,
    title: 'Funding Roadmap',
    description:
      'Programs, eligibility, award ranges, compliance calendar, and go/no-go recommendations across your target funding landscape.',
  },
  {
    icon: Map,
    title: '60-Day Deadline View',
    description:
      'Calendar of upcoming deadlines with priority-coded actions so you know exactly what to submit and when.',
  },
  {
    icon: MessageSquare,
    title: 'Review Session',
    description:
      '60-minute dedicated walkthrough with recommendations, priority ranking, and Q&A.',
  },
];

const steps = [
  { label: 'Review priorities', color: 'bg-indigo-500', ring: 'ring-indigo-100' },
  { label: 'Make a determination', color: 'bg-emerald-500', ring: 'ring-emerald-100' },
  { label: 'Execute plan', color: 'bg-amber-500', ring: 'ring-amber-100' },
];

export default function RoadmapDeliverablesSlide({ mode }: Props) {
  return (
    <Slide mode={mode}>
      <div className="w-full max-w-4xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3 print:mb-1 print:text-[7pt]">
          Deliverables
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-10 print:mb-4 print:text-[16pt]">
          What You Receive
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12 print:grid-cols-3 print:gap-2 print:mb-5">
          {deliverables.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="rounded-xl border border-border/60 p-5 flex flex-col print:p-3 print:rounded-md"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4 print:w-7 print:h-7 print:mb-2">
                <Icon className="w-5 h-5 text-primary print:w-3.5 print:h-3.5" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-2 print:text-[8pt] print:mb-1">{title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed flex-1 print:text-[7pt]">{description}</p>
            </div>
          ))}
        </div>

        <Separator className="mb-8 print:mb-4" />

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-6 print:mb-3 print:text-[7pt]">
          Next Steps
        </p>

        <div className="relative">
          <div className="absolute top-4 left-[16.667%] right-[16.667%] h-0.5 bg-border/40 print:top-3" />
          <div className="grid grid-cols-3 gap-5 relative print:gap-2">
            {steps.map((step, i) => (
              <div key={step.label} className="flex flex-col items-center text-center">
                <div className={`w-8 h-8 rounded-full ${step.color} text-white flex items-center justify-center text-xs font-bold mb-3 ring-4 ${step.ring} print:w-6 print:h-6 print:mb-1.5 print:ring-2 print:text-[7pt]`}>
                  {i + 1}
                </div>
                <span className="text-sm font-medium text-foreground print:text-[8pt]">{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Slide>
  );
}
